import {
  Component,
  HostListener,
  inject,
  Input,
  type OnDestroy,
  type OnInit,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { directMessageId } from '../../../server/services/utils.service';
import { AuthUserService } from '../auth-user.service';
import {
  generateInboxItemPath,
  generateInboxViewPath,
  getInboxRefId,
  type InboxItemType,
  InboxService,
} from '../inbox.service';
import { MessageService } from '../message.service';
import { SettingsService, type ThemeString } from '../settings.service';
import { TitleService } from '../title.service';

dayjs.extend(relativeTime);

@Component({
  selector: 'app-inbox',
  templateUrl: './inbox.component.html',
  styleUrls: ['./inbox.component.scss'],
  standalone: false,
})
export class InboxComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private messageService = inject(MessageService);
  private titleService = inject(TitleService);
  private settingsService = inject(SettingsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private inboxService = inject(InboxService);
  @Input() archive = false;

  routerOutletActivated: boolean;
  inboxItems: InboxItemType[] = [];
  subscriptions = new Subscription();
  themeClass: ThemeString;

  screenWidth: number;

  refId: string | undefined;
  orgId: string;
  authUserId: string;

  orgUsers: { name: string; link: string }[] = [];
  orgUsersArchived: { name: string; link: string }[] = [];

  viewType: 'task' | 'thread' = 'task';

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;

    this.titleService.set('Inbox');

    const themeSubscription = this.settingsService.themeObservable.subscribe(
      (theme) => (this.themeClass = theme)
    );
    this.subscriptions.add(themeSubscription);

    this.setViewType(this.router.url);

    const paramsSub = this.route.params.subscribe((params) => {
      const orgId = params.orgId;
      this.orgId = orgId;
      const userOrgObj = this.authUserService.getAuthUserOrgObj({ orgId });
      this.authUserId = userOrgObj.user._id;
      userOrgObj.org.users.map((u) => {
        const user = {
          name: u.name,
          link: generateInboxViewPath({
            orgId,
            refId: directMessageId(u._id, this.authUserId),
            mode: 'direct',
            archive: false,
          }),
        };
        if (!u.archivedAt) this.orgUsers.push(user);
        else this.orgUsersArchived.push(user);
      });
      this.getInitialInboxes();
    });
    this.subscriptions.add(paramsSub);

    this.routeChangeSubscribe();

    const threadSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart || event instanceof NavigationEnd) {
        this.setViewType(event.url);
      }
    });
    this.subscriptions.add(threadSub);
  }

  @HostListener('window:resize', ['$event']) onResize(event) {
    this.screenWidth = event.target.innerWidth;
  }

  private setViewType(url: string) {
    if (url.split('/')[3] !== 'inbox' && url.split('/')[3] !== 'inbox-archive')
      return; // not in the inbox any more, will soon be destroyed

    const viewType = url.split('/')[4] === 'task' ? 'task' : 'thread';
    if (viewType !== 'task' && viewType !== 'thread') {
      if (viewType !== undefined)
        throw new Error('unknown viewType in inbox: ' + viewType);
    } else this.viewType = viewType;
  }

  private routeChangeSubscribe() {
    this.setPathParamsAndUpdateTitle(this.router.url);

    const routerEventsSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        map((event) => event as NavigationStart)
      )
      .subscribe((event) => {
        this.setPathParamsAndUpdateTitle(event.url);
      });

    this.subscriptions.add(routerEventsSubscription);
  }

  private setPathParamsAndUpdateTitle(path: string) {
    const pathSegments = path.split('?')[0].split('#')[0].split('/');

    if (!pathSegments[3].startsWith('inbox')) return;

    this.orgId = pathSegments[3];
    this.refId = pathSegments[5];
  }

  private getInitialInboxes() {
    const { orgId, authUserId, archive } = this;

    const cachedInboxItemsSubscription = this.inboxService
      .getCachedLatestInboxItems({ orgId, authUserId, archive })
      .pipe(take(1))
      .subscribe((inboxItems) => {
        inboxItems.forEach((i) => this.potentiallyAddInboxItem(i));

        // If we're on the main messages page and we're on desktop
        // Go to the thread at the top of the list if it exists
        if (
          !getInboxRefId({ path: this.router.url }) &&
          window.innerWidth > 768
        ) {
          const topInboxItem = this.inboxItems[0];
          if (topInboxItem) {
            const { refId, inboxMode: mode } = topInboxItem;

            const path = generateInboxViewPath({
              orgId,
              refId,
              mode,
              archive: this.archive,
            });

            this.router.navigate([path], { replaceUrl: true });
          }
        }
      });
    this.subscriptions.add(cachedInboxItemsSubscription);

    // Start the subscription to update the page
    const newInboxSubscription = this.inboxService
      .getInboxObservable({ orgId, authUserId })
      .subscribe((inbox) => this.potentiallyAddInboxItem(inbox));
    this.subscriptions.add(newInboxSubscription);
  }

  private potentiallyAddInboxItem(inboxItem: InboxItemType) {
    const matching =
      inboxItem.inbox &&
      inboxItem.inbox.archived === this.archive &&
      !inboxItem.inbox.deletedAt;

    const index = this.inboxItems.findIndex((i) => i.refId === inboxItem.refId);
    const existing = this.inboxItems[index];
    if (matching) {
      if (existing) {
        existing.time = inboxItem.time;
        if (
          (inboxItem.inboxMode === 'task' ||
            inboxItem.inboxMode === 'thread') &&
          (existing.inboxMode === 'task' || existing.inboxMode === 'thread')
        ) {
          existing.inbox = _.cloneDeep(inboxItem.inbox);
        }
      } else {
        this.inboxItems.push(_.cloneDeep(inboxItem));
      }
      this.sortThreadsByInboxTime();
    } else if (existing) {
      // remove non-matching item
      this.inboxItems.splice(index, 1);
    }
  }

  private sortThreadsByInboxTime() {
    this.inboxItems.sort((a, b) => {
      if (a.time === b.time) return 0;
      // Put new threads at the top
      if (!a.time) return -1;
      if (!b.time) return 1;
      // Put newer threads at the top
      if (a.time > b.time) return -1;
      return 1;
    });
  }

  newThread(mode: 'thread' | 'channel') {
    const threadItem = this.messageService.newThread(mode);
    const { orgId, refId } = threadItem;
    this.router.navigate([generateInboxItemPath({ orgId, refId, mode })]);
  }

  onRouterOutletActivate(event): void {
    this.routerOutletActivated = true;
  }

  onRouterOutletDeactivate(event): void {
    this.routerOutletActivated = false;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
